/**
 * The reveal gate (task 14): when the loading screen is allowed to come down.
 *
 * Babylon SKIPS every mesh whose material is not compiled/uploaded yet — `scene.isReady(true)` is
 * the very predicate it uses (`Mesh.isReady(completeCheck=true)` → `Material.isReadyForSubMesh`).
 * `main.ts` used to hide `#loading` the moment `createWorldScene` resolved, i.e. as soon as the
 * awaited texture downloads landed, BEFORE the render loop had presented a single frame. Those
 * first frames draw nothing but `scene.clearColor`, which is the theme's fog colour — for L1
 * `FOG_COLOR` = rgb(191,196,209), a near-white field behind the start card. On a slow phone
 * (measured: 250 KB/s + 4× CPU, `tools/reveal-probe.mjs`) that blank state lasted seconds after
 * the loading bar looked finished: the owner's "white screen after loading bar".
 *
 * So the reveal waits for what the acceptance actually needs: a frame that has been PRESENTED
 * (`framesRendered` counts `engine.onEndFrameObservable`), with every material ready (the same
 * predicate Babylon renders with) and the IBL arrived (an environment texture that lands later
 * pops the lighting under the card). Bounded by `REVEAL_BUDGET_MS`, so a device whose shaders
 * never finish is never left staring at a loading bar instead of the game.
 *
 * Pure logic, no Babylon import: the predicate and the waiter are unit-testable under vitest's
 * node env with an injected frame emitter and clock (repo precedent: `src/gfx/quality.ts`).
 */

/** Hard ceiling on the wait. Past it the loading screen comes down regardless (reason 'budget'). */
export const REVEAL_BUDGET_MS = 8000;

/**
 * Frames to present before the ready readouts are trusted. A Babylon material's readiness is
 * recorded while it is rendered (`_wasPreviouslyReady`), so the first `isReady(true)` can be a
 * false negative; the second is the one that means something.
 */
export const REVEAL_MIN_FRAMES = 2;

/** Why the loading screen came down: 'painted' = a real frame was on screen, 'budget' = gave up. */
export type RevealReason = 'painted' | 'budget';

/** What the gate looks at. Everything here is a fact read off the live engine/scene/document. */
export interface RevealSnapshot {
  /** Frames the engine has finished presenting (`engine.onEndFrameObservable` count). */
  framesRendered: number;
  /** `scene.isReady(true)` — false while any mesh's material/shadow/RT work is outstanding. */
  sceneReady: boolean;
  /** The IBL has arrived (true when the scene ships no environment texture at all). */
  environmentReady: boolean;
  /** Milliseconds since the gate started watching. */
  elapsedMs: number;
  /** `document.hidden` — a backgrounded tab freezes rAF, so no frame can ever arrive. */
  documentHidden: boolean;
}

export interface RevealDecision {
  reveal: boolean;
  reason: RevealReason | null;
  /** What is still missing ('' once revealed) — the boot readout and the probe print this. */
  waitingFor: string;
}

/**
 * What the boot publishes on `window.__jreb1dBoot` (the same shape as the existing
 * `window.__jreb1dBackend`): the facts `tools/reveal-probe.mjs` asserts against. Written during
 * the wait and frozen at the reveal, so a page that never resolves still reports where it stalled
 * (`waitingFor`) instead of leaving the gate blind.
 */
export interface BootReadout {
  /** Frames the engine has presented so far (frozen at the reveal once the counter is detached). */
  framesRendered: number;
  /** `performance.now()` when the loading screen came down; null while it is still up. */
  revealedAt: number | null;
  reason: RevealReason | null;
  /** What the gate was still waiting on at the reveal ('' when it revealed honestly). */
  waitingFor: string;
  /** Milliseconds spent waiting for the first painted frame (the gate's own cost). */
  waitedMs: number;
  /** `scene.isReady(true)` at the reveal — false only on a budget reveal. */
  sceneReadyAtReveal: boolean;
  /** The IBL had arrived at the reveal — false only on a budget reveal. */
  environmentReadyAtReveal: boolean;
  /** The budget the gate was run with (the probe can tighten it, the code owns the default). */
  budgetMs: number;
}

/**
 * The one rule, as a pure function (the gate's whole contract):
 * reveal when a frame has been presented AND the scene is renderable AND the IBL is in — or when
 * the budget has run out and the page is VISIBLE. A hidden page never reveals: rAF is frozen
 * there, so waiting costs nothing and revealing would spend the gate on nobody watching.
 */
export function decideReveal(
  s: RevealSnapshot,
  budgetMs: number = REVEAL_BUDGET_MS,
  minFrames: number = REVEAL_MIN_FRAMES,
): RevealDecision {
  const waiting: string[] = [];
  if (s.framesRendered < minFrames) waiting.push(`frames<${minFrames}`);
  if (!s.sceneReady) waiting.push('materials');
  if (!s.environmentReady) waiting.push('environment');
  if (waiting.length === 0) return { reveal: true, reason: 'painted', waitingFor: '' };
  if (s.elapsedMs >= budgetMs && !s.documentHidden) {
    return { reveal: true, reason: 'budget', waitingFor: waiting.join('+') };
  }
  return { reveal: false, reason: null, waitingFor: s.documentHidden ? `hidden:${waiting.join('+')}` : waiting.join('+') };
}

/** A snapshot supplier — in the game it reads the live engine/scene; in tests it is a fake. */
export type ReadSnapshot = () => RevealSnapshot;

/**
 * Resolve when `decideReveal` says reveal. Polled on every presented frame (the only clock that
 * matters — readiness changes when something renders) plus one `budgetMs` timer, whose only job is
 * to guarantee resolution when frames never come at all. Re-checking the predicate inside the timer
 * is what keeps a hidden page waiting: the timer fires, the gate still says no, and the reveal
 * happens on the first frame after the tab becomes visible again (when the budget is long gone,
 * with reason 'budget').
 */
export function waitForReveal(
  read: ReadSnapshot,
  onFrame: (notify: () => void) => () => void,
  options: { budgetMs?: number; minFrames?: number; schedule?: (fire: () => void, ms: number) => () => void } = {},
): Promise<RevealDecision> {
  const budgetMs = options.budgetMs ?? REVEAL_BUDGET_MS;
  const minFrames = options.minFrames ?? REVEAL_MIN_FRAMES;
  const schedule = options.schedule ?? ((fire, ms) => { const t = setTimeout(fire, ms); return () => clearTimeout(t); });
  return new Promise<RevealDecision>((resolve) => {
    let settled = false;
    let unsubscribe: (() => void) | null = null;
    const finish = (decision: RevealDecision): void => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      cancel();
      resolve(decision);
    };
    let cancel: () => void = () => {};
    const check = (): void => {
      const decision = decideReveal(read(), budgetMs, minFrames);
      if (decision.reveal) finish(decision);
    };
    cancel = schedule(check, budgetMs);
    unsubscribe = onFrame(check);
    check(); // already ready (a warm page, or a snapshot built before the first frame): no extra wait
  });
}
